/**
 * Diffs2 Page - External API version
 * Fetches diff data from external PricingSaaS API instead of local Supabase
 * MVP: Basic period filter, company search, and list display
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X } from 'lucide-react';
import { ExternalDiffsListTab } from '@/components/diffs/ExternalDiffsListTab';
import { useManagementPeriods } from '@/hooks/useManagementPeriods';
import { getFormattedPeriodDateRange, formatDailyPeriodLabel } from '@/utils/periodFormatting';
import { warmupManagementApi, fetchDiffStats } from '@/utils/externalDiffsAPI';
// @ts-ignore
import Cookies from 'js-cookie';

// Warm up the management API edge function on module load (fire-and-forget)
warmupManagementApi();

const Diffs2 = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Period type filter - default to quarters
  const [periodType, setPeriodType] = useState<'weeks' | 'quarters' | 'years' | 'daily' | 'all'>(() => {
    return (Cookies.get('diffs2_period_type') as any) || 'quarters';
  });

  // Combined status filter: all | published | qa_checked | qa_pending
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'qa_checked' | 'qa_pending' | 'archived'>(() => {
    return (Cookies.get('diffs2_status_filter') as any) || 'all';
  });

  const handleStatusFilterChange = (value: 'all' | 'published' | 'qa_checked' | 'qa_pending' | 'archived') => {
    setStatusFilter(value);
    Cookies.set('diffs2_status_filter', value, { expires: 30 });
  };

  // Sort controls
  const [sortBy, setSortBy] = useState<'created_at' | 'qa_check'>(() => {
    return (Cookies.get('diffs2_sort_by') as any) || 'created_at';
  });

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    return (Cookies.get('diffs2_sort_order') as any) || 'desc';
  });

  const handleSortByChange = (value: 'created_at' | 'qa_check') => {
    setSortBy(value);
    Cookies.set('diffs2_sort_by', value, { expires: 30 });
    // Auto-set status filter to 'qa_checked' when sorting by QA check
    if (value === 'qa_check') {
      handleStatusFilterChange('qa_checked');
    }
  };

  const handleSortOrderChange = (value: 'asc' | 'desc') => {
    setSortOrder(value);
    Cookies.set('diffs2_sort_order', value, { expires: 30 });
  };

  // Period filter - default to latest period
  const [periodFilter, setPeriodFilter] = useState<string>(() => {
    const periodsParam = searchParams.get('periods');
    if (periodsParam) {
      return periodsParam.split(',')[0];
    }
    const savedPeriod = Cookies.get('diffs2_period_filter');
    return savedPeriod || '';
  });

  // Company slug filter (empty = all companies)
  const [companySlug, setCompanySlug] = useState<string>(() => {
    const slug = searchParams.get('slug') || '';
    // Save to cookie
    if (slug) {
      Cookies.set('diffs2_slug_filter', slug, { expires: 30 });
    }
    return slug;
  });

  // Search input value (separate from applied filter)
  const [searchInput, setSearchInput] = useState<string>(() => {
    return searchParams.get('slug') || '';
  });

  // Fetch available periods from Management API
  const { data: availablePeriods, isLoading: periodsLoading, error: periodsError } = useManagementPeriods();

  // Fetch stats for the selected period
  const [periodStats, setPeriodStats] = useState<{
    published_count: number; qa_checked_count: number; qa_pending_count: number; archived_count: number; no_events_count: number;
  } | null>(null);

  useEffect(() => {
    if (!periodFilter) { setPeriodStats(null); return; }
    fetchDiffStats(periodFilter).then(setPeriodStats).catch(() => setPeriodStats(null));
  }, [periodFilter]);

  // Auto-select first period when periods load (only if no period is already selected)
  useEffect(() => {
    if (availablePeriods && availablePeriods.length > 0 && !periodFilter) {
      const filteredPeriods = periodType === 'daily'   ? availablePeriods.filter(p => p.includes('D')) :
                             periodType === 'weeks'   ? availablePeriods.filter(p => p.includes('W')) :
                             periodType === 'quarters' ? availablePeriods.filter(p => p.includes('Q')) :
                             periodType === 'years'   ? availablePeriods.filter(p => p.includes('Y')) :
                             availablePeriods;

      if (filteredPeriods.length > 0 && periodType !== 'all') {
        const firstPeriod = filteredPeriods[0];
        setPeriodFilter(firstPeriod);
        Cookies.set('diffs2_period_filter', firstPeriod, { expires: 30 });
      }
    }
  }, [availablePeriods, periodType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePeriodTypeChange = (type: 'weeks' | 'quarters' | 'years' | 'daily' | 'all') => {
    setPeriodType(type);
    Cookies.set('diffs2_period_type', type, { expires: 30 });

    // Auto-select first period when switching types
    if (type !== 'all' && availablePeriods && availablePeriods.length > 0) {
      const filteredPeriods = type === 'daily'   ? availablePeriods.filter(p => p.includes('D')) :
                             type === 'weeks'   ? availablePeriods.filter(p => p.includes('W')) :
                             type === 'quarters' ? availablePeriods.filter(p => p.includes('Q')) :
                             type === 'years'   ? availablePeriods.filter(p => p.includes('Y')) :
                             availablePeriods;

      if (filteredPeriods.length > 0) {
        const firstPeriod = filteredPeriods[0];
        setPeriodFilter(firstPeriod);
        Cookies.set('diffs2_period_filter', firstPeriod, { expires: 30 });
        return;
      }
    }

    // Clear period filter for 'all' type or if no periods available
    setPeriodFilter('');
    Cookies.remove('diffs2_period_filter');
  };

  const handlePeriodToggle = (period: string) => {
    setPeriodFilter(prev => {
      const newFilter = prev === period ? '' : period;
      if (newFilter) {
        Cookies.set('diffs2_period_filter', newFilter, { expires: 30 });
      } else {
        Cookies.remove('diffs2_period_filter');
      }
      return newFilter;
    });
  };

  const handleSearchClick = () => {
    const slug = searchInput.trim();
    setCompanySlug(slug);
    if (slug) {
      setSearchParams({ slug });
      Cookies.set('diffs2_slug_filter', slug, { expires: 30 });
    } else {
      setSearchParams({});
      Cookies.remove('diffs2_slug_filter');
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setCompanySlug('');
    setSearchParams({});
    Cookies.remove('diffs2_slug_filter');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="py-8">
          {/* Page Header */}
          <div className="mb-6">

            {/* Company Search */}
            {/* <div className="mb-4">
              <Input
                type="text"
                placeholder="Filter by company slug (e.g., 'notion', 'stripe', 'slack')"
                value={companySlug}
                onChange={(e) => handleCompanySlugChange(e.target.value)}
                className="max-w-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                {companySlug ? (
                  <>Currently showing: <span className="font-semibold">{companySlug}</span></>
                ) : (
                  <>Enter a company slug to filter results</>
                )}
              </p>
            </div> */}

            {/* Period Filter - Two Row Layout */}
            {availablePeriods && availablePeriods.length > 0 && (() => {
              const days = availablePeriods.filter(p => p.includes('D'));
              const weeks = availablePeriods.filter(p => p.includes('W'));
              const quarters = availablePeriods.filter(p => p.includes('Q'));
              const years = availablePeriods.filter(p => p.includes('Y'));

              // Get periods for selected type
              const displayPeriods = periodType === 'daily'    ? days :
                                    periodType === 'weeks'    ? weeks :
                                    periodType === 'quarters' ? quarters :
                                    periodType === 'years'    ? years :
                                    availablePeriods;

              return (
                <div className="pb-4 border-b space-y-3">
                  {/* Row 1: Period Type Toggles + Published Filter */}
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge
                      variant={periodType === 'daily' ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-1.5"
                      onClick={() => handlePeriodTypeChange('daily')}
                    >
                      Daily
                    </Badge>
                    <Badge
                      variant={periodType === 'weeks' ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-1.5"
                      onClick={() => handlePeriodTypeChange('weeks')}
                    >
                      Weeks
                    </Badge>
                    <Badge
                      variant={periodType === 'quarters' ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-1.5"
                      onClick={() => handlePeriodTypeChange('quarters')}
                    >
                      Quarters
                    </Badge>
                    <Badge
                      variant={periodType === 'years' ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-1.5"
                      onClick={() => handlePeriodTypeChange('years')}
                    >
                      Years
                    </Badge>
                    <Badge
                      variant={periodType === 'all' ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-1.5"
                      onClick={() => handlePeriodTypeChange('all')}
                    >
                      All
                    </Badge>

                    <span className="text-gray-300 mx-1">|</span>

                    <Badge
                      variant={statusFilter === 'all' ? 'default' : 'outline'}
                      className="cursor-pointer px-3 py-1.5"
                      onClick={() => handleStatusFilterChange('all')}
                    >
                      All{periodStats ? ` (${periodStats.published_count + periodStats.qa_checked_count + periodStats.qa_pending_count + (periodStats.archived_count ?? 0)})` : ''}
                    </Badge>
                    <Badge
                      variant={statusFilter === 'published' ? 'default' : 'outline'}
                      className={`cursor-pointer px-3 py-1.5 ${statusFilter === 'published' ? 'bg-green-600 hover:bg-green-700' : 'text-green-700 border-green-300 hover:bg-green-50'}`}
                      onClick={() => handleStatusFilterChange('published')}
                    >
                      Published{periodStats ? ` (${periodStats.published_count})` : ''}
                    </Badge>
                    <Badge
                      variant={statusFilter === 'qa_checked' ? 'default' : 'outline'}
                      className={`cursor-pointer px-3 py-1.5 ${statusFilter === 'qa_checked' ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}
                      onClick={() => handleStatusFilterChange('qa_checked')}
                    >
                      QA Checked{periodStats ? ` (${periodStats.qa_checked_count})` : ''}
                    </Badge>
                    <Badge
                      variant={statusFilter === 'qa_pending' ? 'default' : 'outline'}
                      className={`cursor-pointer px-3 py-1.5 ${statusFilter === 'qa_pending' ? '' : 'text-gray-600'}`}
                      onClick={() => handleStatusFilterChange('qa_pending')}
                    >
                      QA Pending{periodStats ? ` (${periodStats.qa_pending_count})` : ''}
                    </Badge>
                    {/* Archived: QA looked and concluded the evidence can NEVER support a claim —
                        a blank render, a wrong-company capture, a consent modal over the price row.
                        The historical screenshot is a fixed point in the past and cannot be retaken,
                        so this is terminal, not pending work. It needs to be VISIBLE: an archived
                        diff that simply vanished from the page would be the same silent
                        disappearance we spent today removing from the pipeline. */}
                    <Badge
                      variant={statusFilter === 'archived' ? 'default' : 'outline'}
                      className={`cursor-pointer px-3 py-1.5 ${statusFilter === 'archived' ? 'bg-amber-700 hover:bg-amber-800' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                      onClick={() => handleStatusFilterChange('archived')}
                    >
                      Archived{periodStats ? ` (${periodStats.archived_count ?? 0})` : ''}
                    </Badge>

                  </div>

                  {/* Row 2: Period Selection */}
                  <div className="flex flex-wrap gap-2 items-center">
                    {displayPeriods.slice(0, 12).map(period => (
                      <Badge
                        key={period}
                        variant={periodFilter === period ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handlePeriodToggle(period)}
                      >
                        {period.includes('D') ? formatDailyPeriodLabel(period) : period.replace('Y', '')}
                      </Badge>
                    ))}

                    {displayPeriods.length > 12 && (
                      <Select
                        value={displayPeriods.slice(12).includes(periodFilter) ? periodFilter : ""}
                        onValueChange={(value) => {
                          if (value) {
                            handlePeriodToggle(value);
                          }
                        }}
                      >
                        <SelectTrigger className="h-6 w-auto gap-1 border-0 bg-transparent p-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-50">
                          <Badge
                            variant={displayPeriods.slice(12).includes(periodFilter) ? 'default' : 'outline'}
                            className="cursor-pointer"
                          >
                            {displayPeriods.slice(12).includes(periodFilter)
                              ? (periodFilter.includes('D') ? formatDailyPeriodLabel(periodFilter) : periodFilter.replace('Y', ''))
                              : `+${displayPeriods.length - 12} more`}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {displayPeriods.slice(12).map(period => (
                            <SelectItem key={period} value={period}>
                              {period.includes('D') ? formatDailyPeriodLabel(period) : period.replace('Y', '')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                  </div>
                </div>
              );
            })()}
          </div>

          {/* Period Heading with Date Range */}
          {periodFilter && (
            <div className="mb-6 flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{periodFilter}</h1>
              <span className="text-lg text-gray-500">
                {getFormattedPeriodDateRange(periodFilter)}
              </span>
            </div>
          )}

          {/* Diffs List */}
          <ExternalDiffsListTab
            periodFilter={periodFilter}
            companySlug={companySlug}
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            onSearchSubmit={handleSearchClick}
            onSearchClear={handleClearSearch}
            statusFilter={statusFilter}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        </div>
      </div>
    </div>
  );
};

export default Diffs2;
